/**
 * Classroom Deletion Error Codes — mirrors backend CLASSROOM_DELETE_ERROR_CODES
 * (src/common/constants/classroom-delete-error-codes.ts).
 * Used to distinguish between hard-block and soft-gate 400 responses.
 * Coordinate with the backend team before changing these string values.
 */
export const CLASSROOM_DELETE_ERROR_CODES = {
  // Hard block: exclusive paid events/surveys must be resolved first (no bypass)
  HAS_EXCLUSIVE_PAID_EVENTS: 'CLASSROOM_DELETE_HAS_EXCLUSIVE_PAID_EVENTS',

  // Soft gate: classroom has students — requires confirmed: true to bypass
  HAS_STUDENTS: 'CLASSROOM_DELETE_HAS_STUDENTS',
} as const;

export type ClassroomDeleteErrorCode =
  (typeof CLASSROOM_DELETE_ERROR_CODES)[keyof typeof CLASSROOM_DELETE_ERROR_CODES];

/**
 * Classroom Inactivation Error Codes — mirrors the backend soft-gate contract.
 * Used to distinguish a confirmable inactivation 400 (classroom still has active
 * students) from a generic error so the UI can show the move-to-Unassigned
 * confirmation modal instead of a plain error toast.
 * Coordinate with the backend team before changing these string values.
 */
export const CLASSROOM_INACTIVATE_ERROR_CODES = {
  // Soft gate: classroom has active students — requires confirmed: true to bypass
  HAS_STUDENTS: 'CLASSROOM_INACTIVATE_HAS_STUDENTS',
} as const;

export type ClassroomInactivateErrorCode =
  (typeof CLASSROOM_INACTIVATE_ERROR_CODES)[keyof typeof CLASSROOM_INACTIVATE_ERROR_CODES];

/**
 * Student Inactivation Error Codes — mirrors the backend
 * STUDENT_INACTIVATE_ERROR_CODES. Used to distinguish the unsettled-invoice
 * block from a generic update error so the UI can name the blocker and offer a
 * route to Finance instead of a bare toast.
 *
 * NOTE: this is a HARD block. There is no `confirmed` bypass — the invoice must
 * be settled or cancelled first — so the UI must never offer a "proceed anyway"
 * action for it.
 * Coordinate with the backend team before changing these string values.
 */
export const STUDENT_INACTIVATE_ERROR_CODES = {
  // Hard block: the student still has unsettled invoices (no bypass)
  HAS_PENDING_INVOICES: 'STUDENT_INACTIVATE_HAS_PENDING_INVOICES',
} as const;

export type StudentInactivateErrorCode =
  (typeof STUDENT_INACTIVATE_ERROR_CODES)[keyof typeof STUDENT_INACTIVATE_ERROR_CODES];

/**
 * Student Deletion Error Codes — mirrors the backend
 * STUDENT_DELETE_ERROR_CODES. Deleting a student runs the same teardown as
 * inactivating one (unassign, drop the live re-enrollment record) on top of the
 * deletion flags, so it is gated on the same rule. Kept separate from
 * STUDENT_INACTIVATE_ERROR_CODES because the two actions are confirmed on
 * different screens.
 *
 * NOTE: HARD block — no `confirmed` bypass. Never offer "delete anyway".
 * Coordinate with the backend team before changing these string values.
 */
export const STUDENT_DELETE_ERROR_CODES = {
  // Hard block: the student still has unsettled invoices (no bypass)
  HAS_PENDING_INVOICES: 'STUDENT_DELETE_HAS_PENDING_INVOICES',
} as const;

export type StudentDeleteErrorCode =
  (typeof STUDENT_DELETE_ERROR_CODES)[keyof typeof STUDENT_DELETE_ERROR_CODES];

/**
 * Per-student refusal codes returned inside `errors[]` by the unified
 * re-enrollment transition endpoint. The batch still succeeds for everyone
 * else, so these are reported per row in the transition summary rather than as
 * a page-level error.
 * Coordinate with the backend team before changing these string values.
 */
export const RE_ENROLLMENT_TRANSITION_ERROR_CODES = {
  // Hard block: the student still has unsettled invoices (GRADUATE / DROP_OFF)
  HAS_PENDING_INVOICES: 'ERR_STUDENT_HAS_PENDING_INVOICES',
  // The student's record is already GRADUATED / DROP_OFF — terminal, no re-transition
  ALREADY_LEAVER: 'ERR_STUDENT_ALREADY_LEAVER',
  // The student is not in the classroom the transition was launched from
  WRONG_CLASSROOM: 'ERR_STUDENT_WRONG_CLASSROOM',
} as const;

export type ReEnrollmentTransitionErrorCode =
  (typeof RE_ENROLLMENT_TRANSITION_ERROR_CODES)[keyof typeof RE_ENROLLMENT_TRANSITION_ERROR_CODES];

/**
 * Curriculum Inactivation Error Codes — mirrors the backend status-toggle contract
 * (PATCH /v2/curriculum/:curriculum_id/status). Used to distinguish a hard block
 * (active re-enrollment / active academic year — no bypass) from a confirmable
 * soft gate (curriculum still has active classrooms) so the UI can show the
 * move-to-Unassigned impact confirmation modal instead of a plain error toast.
 * Coordinate with the backend team before changing these string values.
 */
export const CURRICULUM_INACTIVATE_ERROR_CODES = {
  // Hard block: an active re-enrollment cycle references this curriculum (no bypass)
  ACTIVE_RE_ENROLLMENT: 'CURRICULUM_INACTIVATE_ACTIVE_RE_ENROLLMENT',

  // Hard block: an active academic year references this curriculum (no bypass)
  ACTIVE_ACADEMIC_YEAR: 'CURRICULUM_INACTIVATE_ACTIVE_ACADEMIC_YEAR',

  // Soft gate: curriculum has active classrooms — requires confirmed: true to bypass
  HAS_CLASSROOMS: 'CURRICULUM_INACTIVATE_HAS_CLASSROOMS',
} as const;

export type CurriculumInactivateErrorCode =
  (typeof CURRICULUM_INACTIVATE_ERROR_CODES)[keyof typeof CURRICULUM_INACTIVATE_ERROR_CODES];

/**
 * Curriculum Permanent-Deletion Error Codes — mirrors the backend gated cascade
 * delete contract (DELETE /v2/curriculum/:curriculum_id, body { confirmed? }).
 * Used to distinguish three hard blocks (students enrolled / active re-enrollment
 * / active academic year — no bypass) from a confirmable soft gate (requires an
 * impact-report acknowledgement) so the UI can show the correct modal instead of
 * a plain error toast.
 * Coordinate with the backend team before changing these string values.
 */
export const CURRICULUM_DELETE_ERROR_CODES = {
  // Hard block: students are still enrolled under this curriculum (no bypass)
  STUDENTS_ENROLLED: 'CURRICULUM_DELETE_STUDENTS_ENROLLED',

  // Hard block: an active re-enrollment cycle references this curriculum (no bypass)
  ACTIVE_RE_ENROLLMENT: 'CURRICULUM_DELETE_ACTIVE_RE_ENROLLMENT',

  // Hard block: an active academic year references this curriculum (no bypass)
  ACTIVE_ACADEMIC_YEAR: 'CURRICULUM_DELETE_ACTIVE_ACADEMIC_YEAR',

  // Soft gate: deletion cascades to academic config/schedules — requires confirmed: true
  REQUIRES_CONFIRMATION: 'CURRICULUM_DELETE_REQUIRES_CONFIRMATION',
} as const;

export type CurriculumDeleteErrorCode =
  (typeof CURRICULUM_DELETE_ERROR_CODES)[keyof typeof CURRICULUM_DELETE_ERROR_CODES];

/**
 * Subject Assignment Unlink Error Codes — mirrors backend
 * SUBJECT_ASSIGN_DELETE_ERROR_CODES. Used to distinguish a hard block (active
 * exams, no bypass) from a soft gate (teacher/timetable deps, confirmable) so
 * the UI can show the right modal instead of a plain error toast.
 * Coordinate with the backend team before changing these string values.
 */
export const SUBJECT_ASSIGN_DELETE_ERROR_CODES = {
  // Hard block: actively in use for published/in-progress/live exams (no bypass)
  ACTIVE_EXAMS: 'SUBJECT_ASSIGN_DELETE_ACTIVE_EXAMS',

  // Soft gate: teacher mappings and/or timetable schedules — requires confirmed: true
  HAS_DEPENDENCIES: 'SUBJECT_ASSIGN_DELETE_HAS_DEPENDENCIES',
} as const;

export type SubjectAssignDeleteErrorCode =
  (typeof SUBJECT_ASSIGN_DELETE_ERROR_CODES)[keyof typeof SUBJECT_ASSIGN_DELETE_ERROR_CODES];

/**
 * Subject Assignment Inactivation Error Codes — mirrors the backend status-toggle
 * contract (PATCH /v2/subject/assign/:id/status). Distinguishes a hard block
 * (active exams, or an inactive parent subject blocking activation — no bypass)
 * from a confirmable soft gate (teacher/timetable/syllabus/draft-exam deps, all
 * retained) so the UI can show the right modal instead of a plain error toast.
 * Inactivation only flips the assignment's own status.
 * Coordinate with the backend team before changing these string values.
 */
export const SUBJECT_ASSIGN_INACTIVATE_ERROR_CODES = {
  // Hard block: actively in use for published/in-progress/live exams (no bypass)
  ACTIVE_EXAMS: 'SUBJECT_ASSIGN_INACTIVATE_ACTIVE_EXAMS',

  // Soft gate: teacher/timetable/syllabus/draft-exam deps (retained) — requires confirmed: true
  HAS_DEPENDENCIES: 'SUBJECT_ASSIGN_INACTIVATE_HAS_DEPENDENCIES',

  // Hard block: the parent master subject is inactive, so the assignment cannot be activated
  SUBJECT_INACTIVE: 'SUBJECT_ASSIGN_ACTIVATE_SUBJECT_INACTIVE',
} as const;

export type SubjectAssignInactivateErrorCode =
  (typeof SUBJECT_ASSIGN_INACTIVATE_ERROR_CODES)[keyof typeof SUBJECT_ASSIGN_INACTIVATE_ERROR_CODES];

/**
 * Master Subject Deletion Error Codes — mirrors backend SUBJECT_DELETE_ERROR_CODES.
 * Used to distinguish a hard block (active exams — no bypass) from a soft gate
 * (assignment/teacher/timetable/syllabus/draft-exam deps, confirmable) so the UI
 * can show the right modal instead of a plain error toast.
 * Coordinate with the backend team before changing these string values.
 */
export const SUBJECT_DELETE_ERROR_CODES = {
  // Hard block: actively in use for published/in-progress/live exams (no bypass)
  ACTIVE_EXAMS: 'SUBJECT_DELETE_ACTIVE_EXAMS',

  // Soft gate: assignment/teacher/timetable/syllabus/draft-exam deps — requires confirmed: true
  HAS_DEPENDENCIES: 'SUBJECT_DELETE_HAS_DEPENDENCIES',
} as const;

export type SubjectDeleteErrorCode =
  (typeof SUBJECT_DELETE_ERROR_CODES)[keyof typeof SUBJECT_DELETE_ERROR_CODES];

/**
 * Master Subject Inactivation Error Codes — mirrors the backend status-toggle
 * contract (PATCH /v2/subject/:id/status). Distinguishes a hard block (active
 * exams — no bypass) from a confirmable soft gate (assignment/teacher/timetable/
 * syllabus/draft-exam deps, all retained) so the UI can show the right modal
 * instead of a plain error toast. Inactivation only flips the subject status.
 * Coordinate with the backend team before changing these string values.
 */
export const SUBJECT_INACTIVATE_ERROR_CODES = {
  // Hard block: actively in use for published/in-progress/live exams (no bypass)
  ACTIVE_EXAMS: 'SUBJECT_INACTIVATE_ACTIVE_EXAMS',

  // Soft gate: assignment/teacher/timetable/syllabus/draft-exam deps (retained) — requires confirmed: true
  HAS_DEPENDENCIES: 'SUBJECT_INACTIVATE_HAS_DEPENDENCIES',
} as const;

export type SubjectInactivateErrorCode =
  (typeof SUBJECT_INACTIVATE_ERROR_CODES)[keyof typeof SUBJECT_INACTIVATE_ERROR_CODES];

/**
 * Syllabus Permanent-Deletion Error Codes — mirrors backend
 * SYLLABUS_DELETE_ERROR_CODES. Syllabus is a leaf table, so permanent deletion
 * is unconditional (no hard/soft block); the only error code is NOT_FOUND.
 * Coordinate with the backend team before changing these string values.
 */
export const SYLLABUS_DELETE_ERROR_CODES = {
  // The syllabus group does not exist for this organization.
  NOT_FOUND: 'SYLLABUS_DELETE_NOT_FOUND',
} as const;

export type SyllabusDeleteErrorCode =
  (typeof SYLLABUS_DELETE_ERROR_CODES)[keyof typeof SYLLABUS_DELETE_ERROR_CODES];

/**
 * Grade Permanent-Deletion Error Codes — mirrors the backend gated cascade delete
 * contract (DELETE /v2/grade/:id, body { confirmed? }). Distinguishes a hard block
 * (exclusive paid events — no bypass) from a confirmable soft gate (grade still has
 * students) so the UI can show the correct modal instead of a plain error toast.
 * Coordinate with the backend team before changing these string values.
 */
export const GRADE_DELETE_ERROR_CODES = {
  // Hard block: exclusive paid events must be resolved first (no bypass)
  HAS_EXCLUSIVE_PAID_EVENTS: 'GRADE_DELETE_HAS_EXCLUSIVE_PAID_EVENTS',

  // Soft gate: grade has students — requires confirmed: true to bypass
  HAS_STUDENTS: 'GRADE_DELETE_HAS_STUDENTS',
} as const;

export type GradeDeleteErrorCode =
  (typeof GRADE_DELETE_ERROR_CODES)[keyof typeof GRADE_DELETE_ERROR_CODES];

/**
 * Grade Inactivation Error Codes — mirrors the backend status-toggle contract
 * (PUT /v2/grade/status/:id, body { status, confirmed? }; the gate only fires when
 * status: false). A confirmable soft gate (grade still has active students) so the
 * UI can show the move-to-Unassigned impact confirmation modal instead of a plain
 * error toast. Coordinate with the backend team before changing these string values.
 */
export const GRADE_INACTIVATE_ERROR_CODES = {
  // Soft gate: grade has active students — requires confirmed: true to bypass
  HAS_STUDENTS: 'GRADE_INACTIVATE_HAS_STUDENTS',
} as const;

export type GradeInactivateErrorCode =
  (typeof GRADE_INACTIVATE_ERROR_CODES)[keyof typeof GRADE_INACTIVATE_ERROR_CODES];
